/**
 * Global Search API Service
 * Typed client for GET /api/search (F18B tenant-scoped fan-out)
 */

import { ENDPOINTS } from '../../config/api.config';
import { api } from './client';
import logger from '../../utils/logger';

/** @typedef {'document'|'task'|'worker'|'jobsite'|'permit'|'training'|'report'} SearchResultType */

/**
 * @typedef {Object} GlobalSearchResult
 * @property {string} id
 * @property {SearchResultType} resultType
 * @property {string} resultTypeLabel
 * @property {string} title
 * @property {string} subtitle
 * @property {string|null} status
 * @property {string} route
 * @property {number} score
 * @property {Record<string, unknown>} metadata
 */

/**
 * @typedef {Object} GlobalSearchGroup
 * @property {string} collection
 * @property {string} label
 * @property {SearchResultType} resultType
 * @property {string} resultTypeLabel
 * @property {'ok'|'error'} status
 * @property {GlobalSearchResult[]} results
 * @property {{ code: 'COLLECTION_SEARCH_FAILED' }|null} error
 */

/**
 * @typedef {Object} GlobalSearchData
 * @property {string} query
 * @property {number} totalResults
 * @property {GlobalSearchResult[]} results
 * @property {GlobalSearchGroup[]} groups
 */

/**
 * Fetch organization-scoped global search results.
 * @param {{ q: string, limit?: number }} params
 * @returns {Promise<{ success: true, data: GlobalSearchData }>}
 */
export async function fetchGlobalSearch({ q, limit = 5 }) {
  const trimmed = typeof q === 'string' ? q.trim() : '';
  if (!trimmed) {
    throw new Error('Search query is required');
  }

  const params = new URLSearchParams({
    q: trimmed,
    limit: String(limit),
  });

  const endpoint = `${ENDPOINTS.search.global}?${params.toString()}`;

  logger.info('[Global Search API] Fetching results', {
    queryLength: trimmed.length,
    limit,
  });

  try {
    const response = await api.get(endpoint);
    return response;
  } catch (error) {
    const errorMessage = error instanceof Error
      ? error.message
      : (typeof error === 'string' && error ? error : 'Unknown error');
    logger.error('[Global Search API] Request failed', { error: errorMessage });
    throw error;
  }
}
